import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Package, Plus, Truck, CheckCircle, Clock, MapPin, ExternalLink, Pencil, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: "shipped", label: "Shipped", icon: Package, color: "text-blue-400" },
  { value: "in_transit", label: "In Transit", icon: Truck, color: "text-yellow-400" },
  { value: "out_for_delivery", label: "Out for Delivery", icon: MapPin, color: "text-orange-400" },
  { value: "delivered", label: "Delivered", icon: CheckCircle, color: "text-green-400" }
];

const GARMENT_TYPES = [
  "Custom Suit",
  "Custom Shirt",
  "Tuxedo",
  "Wedding Attire",
  "Alterations",
  "Other"
];

const AdminShippingPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    garment_type: "Custom Suit",
    tracking_number: "",
    ship_date: "",
    estimated_arrival: "",
    status: "shipped",
    notes: ""
  });

  useEffect(() => {
    const adminAuth = sessionStorage.getItem("adminAuth");
    if (adminAuth !== "true") {
      navigate("/admin");
    } else {
      setIsAuthenticated(true);
      fetchOrders();
    }
  }, [navigate]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/shipping-orders`);
      // Sort by ship date descending
      const sorted = response.data.sort((a, b) => 
        new Date(b.ship_date) - new Date(a.ship_date)
      );
      setOrders(sorted);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
      toast.error("Failed to load shipping orders");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.tracking_number || !formData.ship_date || !formData.estimated_arrival) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      if (editingOrder) {
        await axios.put(`${API}/shipping-orders/${editingOrder.id}`, formData);
        toast.success("Order updated successfully");
      } else {
        await axios.post(`${API}/shipping-orders`, formData);
        toast.success("Order added successfully");
      }
      
      setShowForm(false);
      setEditingOrder(null);
      resetForm();
      fetchOrders();
    } catch (error) {
      console.error("Failed to save order:", error);
      toast.error("Failed to save order");
    }
  };

  const handleEdit = (order) => {
    setEditingOrder(order);
    setFormData({
      garment_type: order.garment_type,
      tracking_number: order.tracking_number,
      ship_date: order.ship_date,
      estimated_arrival: order.estimated_arrival,
      status: order.status,
      notes: order.notes || ""
    });
    setShowForm(true);
  };

  const handleDelete = async (orderId) => {
    if (!window.confirm("Are you sure you want to delete this order?")) return;
    
    try {
      await axios.delete(`${API}/shipping-orders/${orderId}`);
      toast.success("Order deleted successfully");
      fetchOrders();
    } catch (error) {
      console.error("Failed to delete order:", error);
      toast.error("Failed to delete order");
    }
  };

  const resetForm = () => {
    setFormData({
      garment_type: "Custom Suit",
      tracking_number: "",
      ship_date: "",
      estimated_arrival: "",
      status: "shipped",
      notes: ""
    });
  };

  const getStatusInfo = (status) => {
    return STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  const getDaysUntilArrival = (arrivalDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const arrival = new Date(arrivalDate);
    arrival.setHours(0, 0, 0, 0);
    const diff = Math.ceil((arrival - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="py-8 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <button
              onClick={() => navigate("/admin")}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
            >
              <ArrowLeft size={16} />
              Back to Dashboard
            </button>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Truck size={24} className="text-primary" />
                </div>
                <div>
                  <h1 className="font-serif text-2xl sm:text-3xl tracking-tight">
                    Shipping Tracker
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    Track UPS orders from the factory
                  </p>
                </div>
              </div>
              <Button
                onClick={() => {
                  setEditingOrder(null);
                  resetForm();
                  setShowForm(true);
                }}
                className="bg-primary text-primary-foreground hover:bg-foreground"
              >
                <Plus size={18} className="mr-2" />
                Add Order
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-white/10 p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-xl">
                {editingOrder ? "Edit Order" : "Add New Order"}
              </h2>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingOrder(null);
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Garment Type */}
              <div>
                <label className="block text-sm mb-2">Garment Type *</label>
                <select
                  value={formData.garment_type}
                  onChange={(e) => setFormData({ ...formData, garment_type: e.target.value })}
                  className="w-full bg-background border border-white/10 rounded-md px-3 py-2 text-sm"
                >
                  {GARMENT_TYPES.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Tracking Number */}
              <div>
                <label className="block text-sm mb-2">UPS Tracking Number *</label>
                <Input
                  value={formData.tracking_number}
                  onChange={(e) => setFormData({ ...formData, tracking_number: e.target.value })}
                  placeholder="1Z999AA10123456784"
                  className="bg-background border-white/10"
                />
              </div>

              {/* Ship Date */}
              <div>
                <label className="block text-sm mb-2">Ship Date *</label>
                <Input
                  type="date"
                  value={formData.ship_date}
                  onChange={(e) => setFormData({ ...formData, ship_date: e.target.value })}
                  className="bg-background border-white/10"
                />
              </div>

              {/* Estimated Arrival */}
              <div>
                <label className="block text-sm mb-2">Estimated Arrival *</label>
                <Input
                  type="date"
                  value={formData.estimated_arrival}
                  onChange={(e) => setFormData({ ...formData, estimated_arrival: e.target.value })}
                  className="bg-background border-white/10"
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm mb-2">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full bg-background border border-white/10 rounded-md px-3 py-2 text-sm"
                >
                  {STATUS_OPTIONS.map(status => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm mb-2">Notes (optional)</label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes..."
                  className="bg-background border-white/10"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowForm(false);
                    setEditingOrder(null);
                  }}
                  className="flex-1 border-white/20"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-primary text-primary-foreground hover:bg-foreground"
                >
                  {editingOrder ? "Update" : "Add Order"}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Orders List */}
      <section className="py-8">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent mx-auto mb-3" />
              <p className="text-muted-foreground">Loading orders...</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12 bg-card border border-white/10">
              <Package size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground mb-4">No shipping orders yet</p>
              <Button
                onClick={() => setShowForm(true)}
                variant="outline"
                className="border-white/20"
              >
                <Plus size={16} className="mr-2" />
                Add Your First Order
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((order, index) => {
                const statusInfo = getStatusInfo(order.status);
                const StatusIcon = statusInfo.icon;
                const daysUntil = getDaysUntilArrival(order.estimated_arrival);
                
                return (
                  <motion.div
                    key={order.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`bg-card border p-6 ${
                      order.status === "delivered" 
                        ? "border-green-500/30" 
                        : "border-white/10"
                    }`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      {/* Left: Order Info */}
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          order.status === "delivered" ? "bg-green-500/10" : "bg-primary/10"
                        }`}>
                          <StatusIcon size={20} className={statusInfo.color} />
                        </div>
                        <div>
                          <h3 className="font-medium text-lg">{order.garment_type}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              order.status === "delivered" 
                                ? "bg-green-500/20 text-green-400"
                                : order.status === "out_for_delivery"
                                ? "bg-orange-500/20 text-orange-400"
                                : order.status === "in_transit"
                                ? "bg-yellow-500/20 text-yellow-400"
                                : "bg-blue-500/20 text-blue-400"
                            }`}>
                              {statusInfo.label}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-2 font-mono">
                            {order.tracking_number}
                          </p>
                          {order.notes && (
                            <p className="text-sm text-muted-foreground mt-1">
                              {order.notes}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Center: Dates */}
                      <div className="flex gap-6 md:gap-8">
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground mb-1">Shipped</p>
                          <p className="text-sm">{formatDate(order.ship_date)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground mb-1">Est. Arrival</p>
                          <p className="text-sm font-medium">{formatDate(order.estimated_arrival)}</p>
                          {order.status !== "delivered" && (
                            <p className={`text-xs mt-1 ${
                              daysUntil < 0 ? "text-red-400" : 
                              daysUntil <= 2 ? "text-yellow-400" : "text-muted-foreground"
                            }`}>
                              {daysUntil < 0 
                                ? `${Math.abs(daysUntil)} days overdue`
                                : daysUntil === 0 
                                ? "Arriving today"
                                : `${daysUntil} days`
                              }
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Right: Actions */}
                      <div className="flex items-center gap-2">
                        <a
                          href={`https://www.ups.com/track?tracknum=${order.tracking_number}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-3 py-1.5 bg-[#351c15] text-[#FFB500] hover:bg-[#4a2a1f] transition-colors text-sm font-bold rounded"
                        >
                          <Truck size={16} />
                          UPS Track
                        </a>
                        <button
                          onClick={() => handleEdit(order)}
                          className="p-2 text-muted-foreground hover:text-foreground transition-colors"
                          title="Edit"
                        >
                          <Pencil size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(order.id)}
                          className="p-2 text-muted-foreground hover:text-red-400 transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AdminShippingPage;
